<script lang="ts" setup>
  import {
    AiAugmentationService,
    type TAiAugmentation,
    ImagePresetEnum,
    type ImageproxyImagePreset
  } from '@/types/generated'
  import { computed, onMounted, ref } from 'vue'

  const props = defineProps({
    aiAugmentationUuid: {
      type: String,
      required: false
    }
  })

  const aiAugmentation = ref<TAiAugmentation | null>(null)
  const loading = ref(true)

  onMounted(async () => {
    if (props.aiAugmentationUuid) {
      try {
        loading.value = true
        aiAugmentation.value = await AiAugmentationService.aiAugmentationRetrieve({
          uuid: props.aiAugmentationUuid
        })
      } catch (error) {
        console.error('Error fetching AI augmentation:', error)
      } finally {
        loading.value = false
      }
    } else {
      loading.value = false
    }
  })

  const documentPreview = computed<ImageproxyImagePreset | null>(() => {
    if (!aiAugmentation.value || !aiAugmentation.value.document) return null
    return (
      aiAugmentation.value.document.images?.find(
        (image) => image.image_preset === ImagePresetEnum.LARGE
      ) || null
    )
  })
</script>
<template>
  <div
    v-if="loading"
    class="flex items-center justify-center py-8"
  >
    <div class="loader"></div>
  </div>
  <div
    v-else-if="aiAugmentation"
    class="ai-augmentation-feedback"
  >
    <div class="card rounded border p-4 shadow-sm">
      <h3 class="mb-3 text-lg font-bold">AI Augmentation Details</h3>

      <div class="grid grid-cols-1 gap-3">
        <!-- Augmentation variant -->
        <div class="mb-2">
          <span class="font-semibold">Type:</span>
          <span class="ml-2 rounded-full bg-blue-100 px-2 py-1 text-sm text-blue-800">
            {{ aiAugmentation.augmentation_variant }}
          </span>
        </div>

        <!-- Value -->
        <div class="mb-2">
          <div class="mb-1 font-semibold">Value:</div>
          <div class="rounded border bg-gray-50 p-3">
            <pre
              v-if="typeof aiAugmentation.value === 'object'"
              class="whitespace-pre-wrap text-sm"
              >{{ JSON.stringify(aiAugmentation.value, null, 2) }}</pre
            >
            <span v-else>{{ aiAugmentation.value }}</span>
          </div>
        </div>

        <!-- Explanation -->
        <div
          v-if="aiAugmentation.explanation"
          class="mb-2"
        >
          <div class="mb-1 font-semibold">Explanation:</div>
          <div class="overflow-auto rounded border bg-gray-50 p-3">
            <p class="whitespace-pre-wrap text-sm">{{ aiAugmentation.explanation }}</p>
          </div>
        </div>

        <!-- Referenced Document Pages -->
        <div
          v-if="
            aiAugmentation.referenced_document_pages &&
            aiAugmentation.referenced_document_pages.length > 0
          "
          class="mb-2"
        >
          <div class="mb-1 font-semibold">Referenced Pages:</div>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="page in aiAugmentation.referenced_document_pages"
              :key="page"
              class="rounded bg-gray-100 px-2 py-1 text-sm"
            >
              Page {{ page }}
            </span>
          </div>
        </div>

        <!-- Mapper Keys -->
        <div class="mt-2 grid grid-cols-2 gap-3">
          <div
            v-if="aiAugmentation.analysis_mapper_key"
            class="mb-2"
          >
            <span class="font-semibold">Analysis Mapper Key:</span>
            <div class="mt-1 rounded bg-gray-100 px-2 py-1 text-sm">
              {{ aiAugmentation.analysis_mapper_key }}
            </div>
          </div>

          <div class="mb-2">
            <span class="font-semibold">Augmented Mapper Key:</span>
            <div class="mt-1 rounded bg-gray-100 px-2 py-1 text-sm">
              {{ aiAugmentation.augmented_mapper_key }}
            </div>
          </div>
        </div>

        <!-- UUID for debugging -->
        <div class="mb-4 mt-2 text-xs text-gray-500">
          Augmentation ID: {{ aiAugmentation.uuid }}
        </div>
      </div>

      <!-- Document Preview Section (Moved below analysis) -->
      <div
        v-if="aiAugmentation.document"
        class="mt-6 border-t pt-4"
      >
        <h3 class="mb-3 text-lg font-bold">Document</h3>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <!-- Document Preview -->
          <div
            v-if="documentPreview"
            class="document-panel rounded border p-4 shadow-sm"
          >
            <h4 class="text-md mb-2 font-semibold">Document Preview</h4>
            <div class="document-content">
              <img
                :src="documentPreview.url"
                :alt="aiAugmentation.document?.name || 'Document Preview'"
                class="mb-2 max-w-full"
              />
            </div>
          </div>

          <!-- Document Info -->
          <div class="document-info-panel rounded border p-4 shadow-sm">
            <h4 class="text-md mb-2 font-semibold">Document Info</h4>
            <div class="mb-2">
              <p>
                <span class="font-medium">Name:</span>
                {{ aiAugmentation.document.name }}
              </p>
              <p v-if="aiAugmentation.document.uuid">
                <span class="font-medium">UUID:</span>
                {{ aiAugmentation.document.uuid }}
              </p>
              <p v-if="aiAugmentation.document.created_at">
                <span class="font-medium">Created:</span>
                {{ new Date(aiAugmentation.document.created_at).toLocaleString() }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="p-4 text-center text-gray-500"
  >
    No AI augmentation data available
  </div>
</template>

<style scoped>
  .ai-augmentation-feedback {
    max-width: 100%;
    overflow-x: auto;
  }

  .document-panel {
    max-height: 500px;
    overflow-y: auto;
    background-color: white;
  }

  .document-content {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .document-content img {
    max-height: 400px;
    object-fit: contain;
  }

  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
